import { Injectable } from '@angular/core';
import { CONSTANTS } from 'src/app/core/constants/constants';
import { Line } from 'src/app/core/models/line.model';
import { CurvatureToStress } from 'src/app/core/models/results/fad-flex/curvature-to-stress.enum';
import { FlexibleFatigueGlobalProperties } from 'src/app/core/models/results/fad-flex/flexible-fatigue-global-properties.model';
import { MeanStressCorrection } from 'src/app/core/models/results/fad-flex/mean-stress-correction.enum';
import { LineResultsRequester } from 'src/app/core/models/results/line-results-requester.model';
import { Result } from 'src/app/core/models/results/result.model';
import { Simulation } from 'src/app/core/models/simulation.model';
import { Clone } from 'src/app/shared/utils/clone';
import { IsEqual } from 'src/app/shared/utils/is-equal';
import { DataSharingService } from '../../../data-sharing/data-sharing.service';
import { EnvironmentPropsService } from '../../../environment-props/environment-props.service';
import { AnalysisService } from '../../load/analysis/analysis.service';
import { MapsService } from '../../objects/maps/maps.service';
import { SimulationManagerService } from '../../simulation-manager.service';
import { ConnectorsService } from '../connectors/connectors.service';
import { IntervalsService } from '../intervals/intervals.service';

@Injectable({
  providedIn: 'root',
})
export class LineResultsRequestersService {
  constructor(
    private simulationService: SimulationManagerService,
    private mapsService: MapsService,
    private intervalsService: IntervalsService,
    private connectorsService: ConnectorsService,
    private environmentPropsService: EnvironmentPropsService,
    private dataSharingService: DataSharingService,
    private analysisService: AnalysisService,
  ) {}

  hasLineResultsRequester(): boolean {
    return !!this.result?.lineResultsRequesters.length;
  }

  getByLineId(lineId: number): LineResultsRequester {
    const answer = this.result?.lineResultsRequesters.find((req) => req.lineId === lineId);
    return answer || null;
  }

  getById(id: number): LineResultsRequester {
    const answer = this.mapsService.getObjectById(id);
    return answer || null;
  }

  getRequesterByLineCorrelation(line: Line): LineResultsRequester {
    let result = this.getByLineId(line.internalId);
    result = result || this.create(line);
    return result;
  }

  create(line: Line): LineResultsRequester {
    const newLineResultsRequester = new LineResultsRequester();
    const id = this.simulationService.getNewId();

    newLineResultsRequester.internalId = id;
    newLineResultsRequester.title = `LineResultsRequester-${id}`;
    newLineResultsRequester.lineId = line.internalId;
    newLineResultsRequester.intervals = [];
    newLineResultsRequester.flexibleFatigueGlobalProperties = {
      curvatureToStress: CurvatureToStress.TOTAL_CURVATURE,
      meanStressCorrection: MeanStressCorrection.GERBER,
      histogramDelta: 0.5,
    };

    if (this.result.lineResultsRequesters) {
      this.result.lineResultsRequesters.push(newLineResultsRequester);
    } else {
      this.result.lineResultsRequesters = [newLineResultsRequester];
    }

    this.analysisService.updatePostProcessByRequester(newLineResultsRequester);

    this.mapsService.saveInAllMaps(
      CONSTANTS.RESULT_ENTITIES.LINE_RESULTS_REQUESTER,
      newLineResultsRequester,
    );
    this.simulationService.changeSimulation();
    this.dataSharingService.updateErrorsLog$.next();
    this.dataSharingService.localChanges$.next();
    return newLineResultsRequester;
  }

  saveGlobalFlexibleFatigueProps(lineId: number, payload: FlexibleFatigueGlobalProperties): void {
    const lineResultsRequester = this.getByLineId(lineId);
    if (!lineResultsRequester) {
      return;
    }

    const originalCopy = Clone(lineResultsRequester.flexibleFatigueGlobalProperties);
    Object.assign(originalCopy, payload);
    if (IsEqual(lineResultsRequester.flexibleFatigueGlobalProperties, originalCopy)) {
      return;
    }

    lineResultsRequester.flexibleFatigueGlobalProperties = originalCopy;
    this.mapsService.updateObject(lineResultsRequester);
    this.simulationService.changeSimulation();
    this.dataSharingService.updateErrorsLog$.next();
    this.dataSharingService.localChanges$.next();
  }

  requesterDiffers(requester1: LineResultsRequester, requester2: LineResultsRequester): boolean {
    const tempRequester1 = Clone(requester1);
    const tempRequester2 = Clone(requester2);
    const newRequester = Object.assign(tempRequester1, tempRequester2);
    return !IsEqual(requester1, newRequester);
  }

  save(lineId: number, payload: LineResultsRequester): void {
    const lineResultsRequesteer = this.getByLineId(lineId);
    if (!lineResultsRequesteer) {
      return;
    }

    const requestersAreEquals = !this.requesterDiffers(lineResultsRequesteer, payload);
    if (requestersAreEquals) {
      return;
    }

    Object.assign(lineResultsRequesteer, payload);

    this.intervalsService.save(lineResultsRequesteer, payload.intervals);
    this.connectorsService.save(lineResultsRequesteer, payload.connectors);
    this.analysisService.updatePostProcessByRequester(lineResultsRequesteer);
    this.mapsService.updateObject(lineResultsRequesteer);
    this.simulationService.changeSimulation();
    this.dataSharingService.updateErrorsLog$.next();
    this.dataSharingService.localChanges$.next();
  }

  deleteRequestersByLine(id: number): void {
    if (!this.result?.lineResultsRequesters) {
      return;
    }
    this.result.lineResultsRequesters = this.result.lineResultsRequesters.filter(
      (req) => req.lineId !== id,
    );
  }

  updateInstructionsByLineIds(lineIds: number[]): void {
    if (!this.environmentPropsService.isAnflex) {
      return;
    }
    const pendingRequesters = this.result?.lineResultsRequesters.filter(({ lineId }) =>
      lineIds.includes(lineId),
    );
    pendingRequesters?.forEach((requester) => {
      requester.lineTitle = this.mapsService.getObjectById(requester.lineId)?.title;
      this.intervalsService.save(requester, requester.intervals);
      this.connectorsService.save(requester, requester.connectors, true);
      this.analysisService.updatePostProcessByRequester(requester);
    });
  }

  private get simulation(): Simulation {
    return this.simulationService.simulation;
  }

  private get result(): Result {
    return this.simulation.results?.[0];
  }
}
